import { SmartlistType } from "../api/MediaAPI.types";

import type {
  CalendarClusterEvent,
  CalendarEventWithCluster,
} from "./calendarClusters";
import type { PivotMedia, SourceMedia } from "../api/EncoderAPI.types";
import type {
  DurationFilter,
  GetMediaResponse,
  MediaChildren,
} from "../api/MediaAPI.types";
import type {
  CarouselComponent,
  ComponentWithDataSourceParams,
  ComponentWithUnknownDataSourceParams,
  FastNowParams,
  ImageFormatComponent,
  MosaicComponent,
  MosaicComponentWithIndividualCarousel,
  MostViewedParams,
  MyListComponent,
  PlatformComponent,
  PurchaseComponent,
  ResumeComponent,
  SmartlistDataSourceComponent,
  SmartlistModuleName,
  UserRecommendationComponent,
  WidgetComponent,
} from "../api/ProjectAPI.types";
import type {
  SmartlistAutoUpdated,
  SmartlistItem,
  UpdatedSmartlist,
} from "../api/SmartlistAPI.types";
import type { SelectBoxDisplayItem } from "../components/selectBox/selectBox.types";
import type { CalendarView } from "../types/fast";

export function isImageFormatComponent(
  component: PlatformComponent,
): component is ImageFormatComponent {
  return (component as ImageFormatComponent).type === "image_format";
}

export function isMosaicComponent(
  component: PlatformComponent,
): component is MosaicComponent {
  return (component as MosaicComponent).type === "mosaic";
}

export function isCarouselComponent(
  component: PlatformComponent,
): component is CarouselComponent {
  return (component as CarouselComponent).type === "carousel";
}

export function isSmartlistAutoUpdated(
  smartlist: UpdatedSmartlist,
): smartlist is SmartlistAutoUpdated {
  return (smartlist as SmartlistAutoUpdated).type === SmartlistType.AUTO;
}

export function isWidgetComponent(
  component: PlatformComponent,
): component is WidgetComponent {
  return (component as WidgetComponent).type === "widget";
}

export function isSmartlistItem(
  smarlistItem: SmartlistItem | SelectBoxDisplayItem,
): smarlistItem is SmartlistItem {
  return (
    (smarlistItem as SmartlistItem).content_type !== undefined &&
    (smarlistItem as SmartlistItem).content_uuid !== undefined
  );
}

export function isDurationFilter(updatedDurationFilter: {
  min?: number;
  max?: number;
}): updatedDurationFilter is DurationFilter {
  return (
    typeof updatedDurationFilter.min === "number" &&
    typeof updatedDurationFilter.max === "number"
  );
}

export function isMosaicWithIndividualCarouselComponent(
  component: PlatformComponent,
): component is MosaicComponentWithIndividualCarousel {
  return (
    component.type === "mosaic" && Boolean(component.params.individual_carousel)
  );
}

export function hasSmartlistModuleNameParamsComponent<
  T extends PlatformComponent,
>(
  component: T,
): component is T & {
  static_component: { smartlist_module_name: SmartlistModuleName };
} {
  return Boolean(
    component.static_component &&
    "smartlist_module_name" in component.static_component &&
    component.static_component.smartlist_module_name,
  );
}
export function isMostViewedSmartlist(
  item: ComponentWithUnknownDataSourceParams,
): item is ComponentWithDataSourceParams<MostViewedParams> {
  return item.static_component?.smartlist_module_name === "mostViewed";
}

export function isFastNowSmartlist(
  item: ComponentWithUnknownDataSourceParams,
): item is ComponentWithDataSourceParams<FastNowParams> {
  return (
    item.static_component?.smartlist_module_name === "now" &&
    "themeId" in item.params &&
    item.params.themeId !== undefined
  );
}

export function isSmartlistDataSourceComponent(
  item: ComponentWithUnknownDataSourceParams,
): item is SmartlistDataSourceComponent {
  return (
    item.dynamic_component?.smartlist_uuid !== undefined &&
    item.dynamic_component?.smartlist_uuid !== null
  );
}

interface PotentialSimpleModuleComponent {
  static_component?: {
    smartlist_module_name?: string;
  } | null;
}

export function isMyListComponent(
  item: PotentialSimpleModuleComponent,
): item is MyListComponent {
  return item.static_component?.smartlist_module_name === "my_list";
}

export function isResumeComponent(
  item: PotentialSimpleModuleComponent,
): item is ResumeComponent {
  return item.static_component?.smartlist_module_name === "resume";
}

export function isUserRecommendationComponent(
  item: PotentialSimpleModuleComponent,
): item is UserRecommendationComponent {
  return item.static_component?.smartlist_module_name === "user_recommendation";
}

export function isPurchaseComponent(
  item: PotentialSimpleModuleComponent,
): item is PurchaseComponent {
  return item.static_component?.smartlist_module_name === "purchase";
}

export function isCalendarView(view: string): view is CalendarView {
  return view === "week" || view === "month";
}

export function isCalendarClusterEvent(
  event?: CalendarEventWithCluster,
): event is CalendarClusterEvent {
  return !!event && "itemIdList" in event.resource;
}

export function isChildItem(
  item: GetMediaResponse | MediaChildren,
): item is MediaChildren {
  return "child_classification" in item && item.child_classification !== null;
}

export function isPivotMedia(
  media: SourceMedia | PivotMedia,
): media is PivotMedia {
  return (media as PivotMedia).encoding_status !== undefined;
}
